import { useState, useContext, useEffect, useRef } from "react"
import { LocaleContext } from "contexts"
import axios from "axios"
import cc from "classcat"
import {
  ChatBubbleOvalLeftEllipsisIcon,
  EnvelopeIcon,
  PhoneIcon,
  BuildingOfficeIcon,
} from "@heroicons/react/24/outline"
import {
  XCircleIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/20/solid"
import Spin from "images/spin.svg"
import Modal from "components/molecules/Modal"
import useTranslations from "hooks/useTranslations"
import { siteCountry, siteUrl, contactFormEndpoint } from "utils/env"
import { handleOpenChat } from "utils/browser"
import {
  getPhoneClosureMessage,
  shouldDisablePhone,
} from "utils/phoneClosureConfig"
import { shouldDisableCrisp } from "utils/crispClosureConfig"
import Altcha from "components/atoms/Altcha"
import countries from "../../../../config/i18n"
import { companies } from "../../../../config/company"

const locales = countries[siteCountry] || countries.fi
const company = companies[siteCountry] || companies.fi

const themes = {
  dark: {
    bg: "bg-zinc-800",
    text: "text-zinc-50",
    button:
      "bg-kkm-darkgreen hover:bg-kkm-darkgreen/90 text-zinc-200 focus:bg-kkm-darkgreen/70",
  },
  light: {
    bg: "bg-zinc-50",
    text: "text-zinc-800",
    button:
      "bg-kkm-green hover:bg-kkm-green/90 text-zinc-800  focus:bg-kkm-green/80",
  },
  lightyellow: {
    bg: "bg-zinc-50",
    text: "text-zinc-800",
    button:
      "bg-kkm-yellow hover:bg-kkm-yellow/90 text-zinc-800  focus:bg-kkm-yellow/80",
  },
}

const Content = ({
  Icon,
  text,
  description,
  color,
}: {
  Icon: React.ElementType
  text: string
  description: string
  color: string
}) => {
  return (
    <div className="flex flex-col items-center space-y-4">
      <Icon className={cc(["w-20 ", color])} aria-hidden="true" />
      <h3 className="text-supportiveDark m-0 text-2xl font-medium leading-7">
        {text}
      </h3>
      <p className="m-0 grow whitespace-pre-line text-base text-stone-800">
        {description}
      </p>
    </div>
  )
}

const ContactItem = ({
  type,
  label,
  icon: Icon,
  content,
  closureMessage = undefined,
  href,
  onClick,
  disabled,
  theme = "dark",
}: {
  type: "button" | "link" | "text"
  label: string
  icon: React.ElementType
  content: string
  closureMessage?: string
  href?: string | null
  onClick?: () => void
  disabled?: boolean
  theme?: "dark" | "light" | "lightyellow"
}) => {
  const commonClasses = "flex items-center text-base"
  const interactiveClasses = disabled ? "" : "cursor-pointer"
  const textClasses = disabled ? "text-zinc-400" : ""

  const contentElement = (
    <>
      <Icon
        className={cc(["h-6 w-6 shrink-0", disabled && "text-zinc-400"])}
        aria-hidden="true"
      />
      <div className="ml-3">
        <div className={cc(["whitespace-pre-line", textClasses])}>
          {content}
        </div>
        {closureMessage && (
          <div className="mt-1 text-sm leading-tight text-zinc-400">
            {closureMessage}
          </div>
        )}
      </div>
    </>
  )

  switch (type) {
    case "button":
      return (
        <button
          type="button"
          className={cc([
            "-ml-3 mt-3 w-fit rounded-full py-2 pl-3 pr-5 font-bold",
            themes[theme].button,
          ])}
          onClick={onClick}
        >
          <dt className="sr-only">{label}</dt>
          <dd className={commonClasses}>{contentElement}</dd>
        </button>
      )
    case "link":
      return (
        <div className="mt-3">
          <dt className="sr-only">{label}</dt>
          {disabled ? (
            <dd className={cc([commonClasses, textClasses])}>
              {contentElement}
            </dd>
          ) : (
            <a href={href}>
              <dd className={cc([commonClasses, interactiveClasses])}>
                {contentElement}
              </dd>
            </a>
          )}
        </div>
      )
    case "text":
    default:
      return (
        <div className="mt-3">
          <dt className="sr-only">{label}</dt>
          <dd className={cc([commonClasses, textClasses])}>{contentElement}</dd>
        </div>
      )
  }
}

const Form = ({
  theme = "dark",
}: {
  theme?: "dark" | "light" | "lightyellow"
}) => {
  const [name, setName] = useState("")
  const [email, setEmail] = useState("")
  const [phone, setPhone] = useState("")
  const [message, setMessage] = useState("")
  const [submitState, setSubmitState] = useState("ready")
  const altchaRef = useRef<{ value: string | null }>(null)
  const [showModal, setShowModal] = useState(false)
  type ModalContentShape = {
    Icon: typeof XCircleIcon
    text: string
    color: string
    description: string
  }
  const [modalContent, setModalContent] = useState<ModalContentShape | "">({
    Icon: XCircleIcon,
    text: "",
    color: "",
    description: "",
  })

  const {
    contactFormName,
    contactFormEmail,
    contactFormPhone,
    contactFormMessage,
    contactFormButtonSubmit,
    contactFormSuccess,
    contactFormError,
    contactFormErrorContact,
  } = useTranslations()

  const successfulSubmission = {
    text: contactFormSuccess,
    description: "",
    Icon: CheckCircleIcon,
    color: "text-kkm-green",
  }

  const errorSubmission = {
    text: contactFormError,
    description: contactFormErrorContact,
    Icon: ExclamationCircleIcon,
    color: "text-red-500",
  }

  useEffect(() => {
    if (!showModal) {
      setTimeout(() => {
        setModalContent("")
      }, 300)
    }
  }, [showModal])

  useEffect(() => {
    if (submitState === "success") {
      setModalContent(successfulSubmission)
    }
    if (submitState === "error") {
      setModalContent(errorSubmission)
    }
  }, [submitState])

  const closeModal = () => {
    setShowModal(false)
  }

  const resetFields = () => {
    setName("")
    setEmail("")
    setPhone("")
    setMessage("")
  }

  const sendForm = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    let res
    setSubmitState("loading")
    const altcha = altchaRef.current?.value

    try {
      res = await axios.post(`${contactFormEndpoint}`, {
        name,
        email,
        phone,
        message,
        altcha,
        source: siteUrl || siteCountry,
      })
      if (res.status === 200) {
        setSubmitState("success")
        setShowModal(true)
        resetFields()
      }
    } catch (err) {
      setSubmitState("error")
      setShowModal(true)
      console.log("Error sending contact form", err)
      setTimeout(() => {
        setSubmitState("ready")
      }, 3000)
    }
  }

  const handleNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setName(event.target.value)
  }

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(event.target.value)
  }

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setPhone(event.target.value)
  }

  const handleMessageChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
  ) => {
    setMessage(event.target.value)
  }

  return (
    <div className="">
      <form onSubmit={sendForm} className="m-0 grid grid-cols-1 gap-y-6">
        <div>
          <label htmlFor="name" className="sr-only">
            {contactFormName}
          </label>
          <input
            type="text"
            name="name"
            id="name"
            autoComplete="name"
            className="block w-full rounded-md border-zinc-300 px-4 py-3 placeholder-zinc-500 shadow-sm focus:border-teal-500 focus:ring-teal-500"
            placeholder={contactFormName}
            value={name}
            onChange={handleNameChange}
          />
        </div>
        <div>
          <label htmlFor="email" className="sr-only">
            {contactFormEmail}
          </label>
          <input
            id="email"
            name="email"
            type="email"
            autoComplete="email"
            className="block w-full rounded-md border-zinc-300 px-4 py-3 placeholder-zinc-500 shadow-sm focus:border-teal-500 focus:ring-teal-500"
            placeholder={contactFormEmail}
            value={email}
            onChange={handleEmailChange}
          />
        </div>
        <div>
          <label htmlFor="phone" className="sr-only">
            {contactFormPhone}
          </label>
          <input
            id="phone"
            type="tel"
            name="phone"
            autoComplete="tel"
            className="block w-full rounded-md border-zinc-300 px-4 py-3 placeholder-zinc-500 shadow-sm focus:border-teal-500 focus:ring-teal-500"
            placeholder={contactFormPhone}
            value={phone}
            onChange={handlePhoneChange}
          />
        </div>
        <div>
          <label htmlFor="message" className="sr-only">
            {contactFormMessage}
          </label>
          <textarea
            name="message"
            rows={4}
            className="block w-full rounded-md border border-zinc-300 px-4 py-3 placeholder-zinc-500 shadow-sm focus:border-teal-500 focus:ring-teal-500"
            placeholder={contactFormMessage}
            value={message}
            onChange={handleMessageChange}
          />
        </div>
        <Altcha
          ref={altchaRef}
          style={
            theme === "dark"
              ? {
                  "--altcha-border-radius": "0.375rem",
                  "--altcha-color-border": "#52525b",
                  "--altcha-color-border-focus": "#2dd4bf",
                  "--altcha-color-active": "teal",
                  "--altcha-color-text": "#e4e4e7",
                  "--altcha-color-base": "rgba(63, 63, 70, 0.5)",
                }
              : {
                  "--altcha-border-radius": "0.375rem",
                  "--altcha-color-border": "#dee0e7",
                  "--altcha-color-active": "teal",
                }
          }
        />
        <div className="mt-3 max-w-xs rounded-full shadow sm:mt-0 sm:shrink-0">
          <button
            className={cc([
              "flex w-full items-center justify-center rounded-full px-5 py-3 text-base font-bold leading-6 transition-colors duration-150 ease-in-out focus:outline-none",
              themes[theme].button,
              {
                "bg-red-500 focus:bg-red-400": submitState === "error",
                "bg-kkm-green focus:bg-kkm-green": submitState === "success",
              },
            ])}
            type="submit"
          >
            {submitState === "loading" && (
              <Spin className="absolute h-5 w-5 animate-spin text-white transition-opacity" />
            )}

            <CheckCircleIcon
              className={cc([
                "absolute h-6 w-6 text-white transition-opacity",
                {
                  "opacity-100": submitState === "success",
                  "opacity-0": submitState !== "success",
                },
              ])}
            />

            {submitState === "error" && (
              <XCircleIcon className="absolute h-6 w-6 text-white transition-opacity" />
            )}
            <span
              className={cc([
                "mx-2 inline-block opacity-0 transition-opacity",
                {
                  "opacity-100": submitState === "ready",
                },
              ])}
            >
              {contactFormButtonSubmit}
            </span>
          </button>
        </div>
      </form>
      <Modal
        isOpen={showModal}
        onRequestClose={closeModal}
        showCloseButton
        closeOnClickAway
      >
        {typeof modalContent !== "string" ? (
          <Content {...modalContent} />
        ) : null}
      </Modal>
    </div>
  )
}

export default function Contact({
  theme = "dark",
  children = null,
  heading = null,
  description = null,
  descriptionSubheading = null,
  descriptionSecond = null,
  hideOfficeInfo = false,
  image = null,
  imageContainerClassName = "",
}: {
  theme?: "dark" | "light" | "lightyellow"
  children?: React.ReactNode
  heading?: string | null
  description?: string | null
  descriptionSubheading?: string | null
  descriptionSecond?: string | null
  hideOfficeInfo?: boolean
  image?: React.ReactNode
  imageContainerClassName?: string
}) {
  const { locale } = useContext(LocaleContext)

  const {
    contactFormHeading,
    contactFormCountry,
    contactFormOffice,
    contactFormChat,
  } = useTranslations()

  const displayHeading = heading ?? contactFormHeading

  const phoneClosureMessage = getPhoneClosureMessage(locale)
  const isPhoneDisabled = shouldDisablePhone()
  // Sweden's chat is closed for the summer-2026 window — hide the entry point.
  const isChatDisabled = shouldDisableCrisp()

  const contactItems: Array<React.ComponentProps<typeof ContactItem>> = [
    ...(siteCountry !== "global" && !isChatDisabled
      ? [
          {
            type: "button" as const,
            label: "Chat",
            icon: ChatBubbleOvalLeftEllipsisIcon,
            content: contactFormChat,
            onClick: handleOpenChat,
          },
        ]
      : []),
    {
      type: "link" as const,
      label: "Email",
      icon: EnvelopeIcon,
      content: locales[locale].contactEmail,
      href: `mailto:${locales[locale].contactEmail}`,
    },
    ...(siteCountry !== "global"
      ? [
          {
            type: (isPhoneDisabled ? "text" : "link") as "text" | "link",
            label: "Phone number",
            icon: PhoneIcon,
            content: locales[locale].contactPhone,
            closureMessage: phoneClosureMessage,
            href: isPhoneDisabled
              ? null
              : `tel:${locales[locale].contactPhone}`,
            disabled: isPhoneDisabled,
          },
          {
            type: "link" as const,
            label: "Address",
            icon: BuildingOfficeIcon,
            // Street/postcode/city come from config/company.js so the office
            // address lives in one place; the country name stays localised.
            content: `${company.address.street},\n${company.address.postalCode} ${company.address.city}, ${contactFormCountry}`,
            href: `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
              `${company.address.street} ${company.address.postalCode} ${company.address.city} ${contactFormCountry}`,
            )}`,
          },
        ]
      : []),
  ]

  return (
    <div
      className={cc(["relative  py-16 lg:py-24", themes[theme].bg])}
      id="contact"
    >
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {image ? (
          <div className="flex flex-col gap-8 lg:flex-row lg:items-stretch lg:gap-12">
            <div
              className={cc([
                "hidden shrink-0 overflow-hidden rounded-2xl lg:block lg:w-96 xl:w-[28rem]",
                imageContainerClassName,
              ])}
            >
              {image}
            </div>
            <div className="min-w-0 flex-1 lg:order-2">
              <h2
                className={cc([
                  "text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl",
                  themes[theme].text,
                ])}
              >
                {displayHeading}
              </h2>
              {description ? (
                <>
                  <p
                    className={cc([
                      "mt-4 max-w-2xl whitespace-pre-line text-lg leading-relaxed",
                      theme === "dark" ? "text-zinc-300" : "text-zinc-600",
                    ])}
                  >
                    {description}
                  </p>
                  {descriptionSubheading ? (
                    <h3
                      className={cc([
                        "mt-6 max-w-2xl text-xl font-semibold",
                        theme === "dark" ? "text-zinc-100" : "text-zinc-800",
                      ])}
                    >
                      {descriptionSubheading}
                    </h3>
                  ) : null}
                  {descriptionSecond ? (
                    <p
                      className={cc([
                        "mt-2 max-w-2xl whitespace-pre-line text-lg leading-relaxed",
                        theme === "dark" ? "text-zinc-300" : "text-zinc-600",
                      ])}
                    >
                      {descriptionSecond}
                    </p>
                  ) : null}
                </>
              ) : null}
              <div className="mt-12">
                <div className="max-w-lg sm:max-w-xl">
                  <Form theme={theme} />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <>
            <h2
              className={cc([
                "text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl",
                themes[theme].text,
              ])}
            >
              {displayHeading}
            </h2>
            {description ? (
              <>
                <p
                  className={cc([
                    "mt-4 max-w-2xl whitespace-pre-line text-lg leading-relaxed",
                    theme === "dark" ? "text-zinc-300" : "text-zinc-600",
                  ])}
                >
                  {description}
                </p>
                {descriptionSubheading ? (
                  <h3
                    className={cc([
                      "mt-6 max-w-2xl text-xl font-semibold",
                      theme === "dark" ? "text-zinc-100" : "text-zinc-800",
                    ])}
                  >
                    {descriptionSubheading}
                  </h3>
                ) : null}
                {descriptionSecond ? (
                  <p
                    className={cc([
                      "mt-2 max-w-2xl whitespace-pre-line text-lg leading-relaxed",
                      theme === "dark" ? "text-zinc-300" : "text-zinc-600",
                    ])}
                  >
                    {descriptionSecond}
                  </p>
                ) : null}
              </>
            ) : null}
            <div
              className={cc([
                "mt-12 space-y-12 md:grid md:gap-16 md:space-y-0",
                hideOfficeInfo ? "md:grid-cols-1" : "md:grid-cols-3",
              ])}
            >
              {!hideOfficeInfo ? (
                <div
                  className={cc([
                    "max-w-xl space-y-10 sm:flex sm:space-x-10 sm:space-y-0 md:flex-col md:space-x-0 md:space-y-6",
                    siteCountry === "global" ? "md:order-2" : "",
                  ])}
                >
                  <div className={cc(["space-y-3", themes[theme].text])}>
                    <h3
                      className={cc([
                        "mb-0 text-lg font-extrabold tracking-tight sm:text-xl",
                        themes[theme].text,
                      ])}
                    >
                      {contactFormOffice}
                    </h3>
                    <dl>
                      {contactItems.map((item) => (
                        <ContactItem key={item.label} {...item} theme={theme} />
                      ))}
                    </dl>
                  </div>
                  {children}
                </div>
              ) : null}
              <div
                className={cc([
                  hideOfficeInfo ? "md:col-span-1" : "md:col-span-2",
                  siteCountry === "global" && !hideOfficeInfo
                    ? "md:order-1"
                    : "",
                ])}
              >
                <div className="h-full max-w-lg sm:max-w-xl">
                  <Form theme={theme} />
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
